import { http } from './http';

/**
 * Cliente del backend de Authoring (Evaluación y Contenido).
 * Reemplaza la persistencia en localStorage por la API real de Laravel.
 * Todos los endpoints cuelgan de /api/authoring y están protegidos por IAM.
 */

const base = '/api/authoring';

export const questionsApi = {
  list: (params = {}) => http.get(`${base}/questions`, { params }).then(r => r.data),
  trashed: (params = {}) => http.get(`${base}/questions/trashed`, { params }).then(r => r.data),
  create: (payload) => http.post(`${base}/questions`, payload).then(r => r.data),
  update: (id, payload) => http.put(`${base}/questions/${id}`, payload).then(r => r.data),
  remove: (id) => http.delete(`${base}/questions/${id}`),
  restore: (id) => http.post(`${base}/questions/${id}/restore`).then(r => r.data),
  duplicate: (id) => http.post(`${base}/questions/${id}/duplicate`).then(r => r.data),
  submitReview: (id) => http.post(`${base}/questions/${id}/submit-review`).then(r => r.data),
  publish: (id) => http.post(`${base}/questions/${id}/publish`).then(r => r.data),
  versions: (id) => http.get(`${base}/questions/${id}/versions`).then(r => r.data),
  bulkAssign: (payload) => http.post(`${base}/questions/bulk-assign`, payload).then(r => r.data),
  bulkUpdate: (payload) => http.post(`${base}/questions/bulk-update`, payload).then(r => r.data),
  bulkMove: (payload) => http.post(`${base}/questions/bulk-move`, payload).then(r => r.data),
  exportUrl: () => `${base}/questions/export`,
};

export const referencesApi = {
  list: (questionId) => http.get(`${base}/questions/${questionId}/references`).then(r => r.data),
  create: (questionId, payload) => http.post(`${base}/questions/${questionId}/references`, payload).then(r => r.data),
  update: (questionId, id, payload) => http.put(`${base}/questions/${questionId}/references/${id}`, payload).then(r => r.data),
  remove: (questionId, id) => http.delete(`${base}/questions/${questionId}/references/${id}`),
};

export const commentsApi = {
  list: (questionId, params = {}) => http.get(`${base}/questions/${questionId}/comments`, { params }).then(r => r.data),
  create: (questionId, payload) => http.post(`${base}/questions/${questionId}/comments`, payload).then(r => r.data),
  update: (questionId, id, payload) => http.put(`${base}/questions/${questionId}/comments/${id}`, payload).then(r => r.data),
  resolve: (questionId, id, resolved = true) => http.post(`${base}/questions/${questionId}/comments/${id}/resolve`, { resolved }).then(r => r.data),
  remove: (questionId, id) => http.delete(`${base}/questions/${questionId}/comments/${id}`),
};

export const testsApi = {
  list: (params = {}) => http.get(`${base}/tests`, { params }).then(r => r.data),
  trashed: (params = {}) => http.get(`${base}/tests/trashed`, { params }).then(r => r.data),
  create: (payload) => http.post(`${base}/tests`, payload).then(r => r.data),
  update: (id, payload) => http.put(`${base}/tests/${id}`, payload).then(r => r.data),
  bulkEdit: (id, items) => http.post(`${base}/tests/${id}/bulk-edit`, { items }).then(r => r.data),
  preview: (id) => http.get(`${base}/tests/${id}/preview`).then(r => r.data),
  duplicate: (id) => http.post(`${base}/tests/${id}/duplicate`).then(r => r.data),
  remove: (id) => http.delete(`${base}/tests/${id}`),
  restore: (id) => http.post(`${base}/tests/${id}/restore`).then(r => r.data),
  submitReview: (id) => http.post(`${base}/tests/${id}/submit-review`).then(r => r.data),
  publish: (id) => http.post(`${base}/tests/${id}/publish`).then(r => r.data),
  archive: (id) => http.post(`${base}/tests/${id}/archive`).then(r => r.data),
};

export const skillgroupsApi = {
  list: (testId) => http.get(`${base}/tests/${testId}/skillgroups`).then(r => r.data),
  create: (testId, payload) => http.post(`${base}/tests/${testId}/skillgroups`, payload).then(r => r.data),
  update: (testId, id, payload) => http.put(`${base}/tests/${testId}/skillgroups/${id}`, payload).then(r => r.data),
  remove: (testId, id) => http.delete(`${base}/tests/${testId}/skillgroups/${id}`),
  bulk: (testId, payload) => http.post(`${base}/tests/${testId}/skillgroups/bulk`, payload).then(r => r.data),
};

export const tagsApi = {
  listSets: () => http.get(`${base}/tag-sets`).then(r => r.data),
  createSet: (payload) => http.post(`${base}/tag-sets`, payload).then(r => r.data),
  updateSet: (id, payload) => http.put(`${base}/tag-sets/${id}`, payload).then(r => r.data),
  removeSet: (id) => http.delete(`${base}/tag-sets/${id}`),
  list: (params = {}) => http.get(`${base}/tags`, { params }).then(r => r.data),
  create: (payload) => http.post(`${base}/tags`, payload).then(r => r.data),
  update: (id, payload) => http.put(`${base}/tags/${id}`, payload).then(r => r.data),
  remove: (id) => http.delete(`${base}/tags/${id}`),
};

export const competenciesApi = {
  list: () => http.get(`${base}/competencies`).then(r => r.data),
  create: (payload) => http.post(`${base}/competencies`, payload).then(r => r.data),
  update: (id, payload) => http.put(`${base}/competencies/${id}`, payload).then(r => r.data),
  remove: (id) => http.delete(`${base}/competencies/${id}`),
  sync: () => http.post(`${base}/competencies/sync`).then(r => r.data),
};

export const aiApi = {
  status: () => http.get(`${base}/ai/status`).then(r => r.data),
  generateQuestion: (payload) => http.post(`${base}/ai/generate-question`, payload).then(r => r.data),
  suggestDistractors: (payload) => http.post(`${base}/ai/suggest-distractors`, payload).then(r => r.data),
  writeExplanation: (payload) => http.post(`${base}/ai/write-explanation`, payload).then(r => r.data),
};

export const blueprintsApi = {
  history: (params = {}) => http.get(`${base}/blueprints/history`, { params }).then(r => r.data),
  forTest: (testId) => http.get(`${base}/tests/${testId}/blueprint-history`).then(r => r.data),
  create: (testId, payload) => http.post(`${base}/tests/${testId}/blueprint-history`, payload).then(r => r.data),
};

export default {
  questions: questionsApi,
  references: referencesApi,
  comments: commentsApi,
  tests: testsApi,
  skillgroups: skillgroupsApi,
  tags: tagsApi,
  competencies: competenciesApi,
  ai: aiApi,
  blueprints: blueprintsApi,
};
